import React from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Box,
  Input,
  InputGroup,
  InputRightElement,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, SmallCloseIcon, SearchIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

interface Option {
  value: any;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value?: any;
  onChange: (value: any) => void;
  placeholder?: string;
  width?: string | number;
  height?: string | number;
  borderRadius?: any;
  mt?: any;
  placement?: any;
  isSearchable?: boolean;
  onSearch?: (term: string) => void;
  searchPlaceholder?: string;
  onScrollEnd?: () => void;
  [key: string]: any;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  width = "fit-content",
  height = "44px",
  mt = 1,
  borderRadius = "100px",
  placement = "bottom-start",
  isDisabled = false,
  isSearchable = false,
  onSearch,
  searchPlaceholder,
  onScrollEnd,
  ...rest
}) => {
  const { t } = useTranslation("common");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const latestOnSearch = React.useRef(onSearch);
  React.useEffect(() => {
    latestOnSearch.current = onSearch;
  }, [onSearch]);

  React.useEffect(() => {
    if (!latestOnSearch.current) return;
    const timer = setTimeout(() => {
      if (latestOnSearch.current) {
        latestOnSearch.current(searchTerm);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Focus the search input when popover opens
  React.useEffect(() => {
    if (isOpen && isSearchable) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen, isSearchable]);

  const filteredOptions = React.useMemo(() => {
    const filtered = searchTerm
      ? options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;
    return [...filtered].sort((a, b) =>
      a.label.localeCompare(b.label, ["ko", "en"], { sensitivity: "base" })
    );
  }, [options, searchTerm]);

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!onScrollEnd) return;
      const target = e.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 40;
      if (nearBottom) {
        onScrollEnd();
      }
    },
    [onScrollEnd]
  );

  const handleSelect = (val: any) => {
    onChange(val);
    onClose();
  };

  const itemStyle = (isSelected: boolean) => ({
    fontWeight: 400,
    color: isSelected ? "#1819B4" : "#222",
    borderRadius: "12px",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    background: "white",
  });

  return (
    <Box position="relative" display="inline-flex" alignItems="center">
      <Popover
        isOpen={isOpen && !isDisabled}
        onClose={onClose}
        placement={placement}
        autoFocus={false}
        closeOnBlur={true}
        offset={[0, 4]}
      >
        <PopoverTrigger>
          <Box
            as="div"
            onClick={() => !isDisabled && (isOpen ? onClose() : onOpen())}
            style={{
              padding: value && !isDisabled ? "8px 36px 8px 16px" : "8px 16px",
              borderRadius: borderRadius,
              border: `1px solid ${
                isOpen
                  ? "#1819B4"
                  : isDisabled
                  ? "#E4EBF0"
                  : value
                  ? "#1819B4"
                  : "#A7B2BA"
              }`,
              background: isDisabled ? "#F8F9FA" : value ? "#EEF2FF" : "#fff",
              height: height,
              fontWeight: value ? 500 : 400,
              fontSize: "16px",
              color: isDisabled ? "#A7B2BA" : value ? "#1819B4" : "#5C6670",
              width: width,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: isDisabled ? "not-allowed" : "pointer",
            }}
          >
            <Box width="100%" display="flex" alignItems="center">
              <Box
                flex={1}
                textAlign="left"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {options?.find((o) => String(o.value) === String(value))?.label || placeholder}
              </Box>
              {(!value || isOpen) && (
                <ChevronDownIcon
                  style={{
                    marginLeft: 8,
                    color: isDisabled ? "#A7B2BA" : "#5C6670",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              )}
            </Box>
          </Box>
        </PopoverTrigger>

        <PopoverContent
          minW="230px"
          maxW="400px"
          border="1px solid #CDD6DC"
          borderRadius="12px"
          boxShadow="4px 4px 8px 0px #0000000F"
          py={0}
          px={0}
          mt={mt}
          bg="white"
          zIndex={9999}
          _focus={{ outline: "none" }}
        >
          {isSearchable && (
            <Box
              px={3}
              pt={3}
              pb={2}
              borderBottom="1px solid #E4EBF0"
            >
              <InputGroup size="sm" justifyContent="center">
                <Input
                  ref={searchInputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder || t("common.search", "Search...")}
                  borderRadius="8px"
                  border="1px solid #CDD6DC"
                  height="32px"
                  fontSize="14px"
                  bg="#F8F9FA"
                  _focus={{ borderColor: "#1819B4", bg: "white", boxShadow: "0 0 0 1px #1819B4" }}
                  _placeholder={{ color: "#A7B2BA" }}
                />
                <InputRightElement height="100%" display="flex" alignItems="center" pointerEvents="none">
                  <SearchIcon color="#A7B2BA" boxSize={3} />
                </InputRightElement>
              </InputGroup>
            </Box>
          )}

          <Box
            maxH="260px"
            overflowY="auto"
            onScroll={handleScroll}
            sx={{
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "#F5F7FA", borderRadius: "12px" },
              "&::-webkit-scrollbar-thumb": {
                background: "#CDD6DC",
                borderRadius: "12px",
                "&:hover": { background: "#A7B2BA" },
              },
            }}
          >
            {/* "All" option */}
            <Box
              style={itemStyle(value === "")}
              _hover={{ bg: "#F5F7FA" }}
              onClick={() => handleSelect("")}
            >
              {t("userStatus.all")}
              {value === "" && (
                <span style={{ marginLeft: 8, color: "#1819B4", fontSize: 18 }}>&#10003;</span>
              )}
            </Box>

            {filteredOptions.map((option) => (
              <Box
                key={option.value}
                style={itemStyle(String(value) === String(option.value))}
                _hover={{ bg: "#F5F7FA" }}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
                {String(value) === String(option.value) && (
                  <span style={{ marginLeft: 8, color: "#1819B4", fontSize: 18 }}>&#10003;</span>
                )}
              </Box>
            ))}
          </Box>
        </PopoverContent>
      </Popover>

      {value && !isDisabled && (
        <Box
          position="absolute"
          right="12px"
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
          cursor="pointer"
          display="flex"
          alignItems="center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange("");
          }}
        >
          <SmallCloseIcon color="#1819B4" />
        </Box>
      )}
    </Box>
  );
};

export default CustomSelect;
